import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import DeleteTagButton from 'tags/components/DeleteTagButton.component';
import EmojiPicker from 'tags/components/EmojiPicker.component';
import SaveTagButton from 'tags/components/SaveTagButton.component';
import TagDescription from 'tags/components/TagDescription.component';
import TagName from 'tags/components/TagName.component';
import UndoTagButton from 'tags/components/UndoTagButton.component';

import { useTranslation } from 'providers/GILT.provider';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import isEmoji from 'utils/predicates/isEmoji.util';
import { capitalize } from 'utils/strings/manipulate.util';

import CN from './ClientTags.module.scss';

const CustomClientTag = ({
  calendarMode,
  deleteTag,
  description,
  emoticon,
  id,
  isTagSaved,
  name,
  numberOfClients,
  salonId,
  saveTag,
  setIsTagSaved,
  setUnsavedChanges,
  validateName,
}) => {
  const { t } = useTranslation();
  const [disableSaveBtn, setDisableSaveBtn] = useState(name.length < 1);
  const [emoji, setEmoji] = useState(emoticon);
  const [isEdited, setIsEdited] = useState(true);
  const [isValidName, setIsValidName] = useState(true);
  const [isEmojiSelected, setIsEmojiSelected] = useState(isEmoji(emoticon) || isEmoji(emoji));
  const [tagName, setTagName] = useState(name);
  const [tagDescription, setTagDescription] = useState(description);

  const addTagsLayoutClasses = cn({
    [CN.container]: true,
    [CN.dark]: calendarMode === 'dark',
    'light-class': calendarMode === 'light',
    'dark-class': calendarMode === 'dark',
    'bright-class': calendarMode !== 'light' && calendarMode !== 'dark',
  });

  const handleName = useCallback(
    ({ target }) => {
      const name = target.value;
      setDisableSaveBtn(fromAnyToBool(name.length < 1));
      setIsEdited(false);
      setIsValidName(validateName({ name, id }));
      setTagName(capitalize(name));
      setUnsavedChanges(true);
    },
    [validateName, id, setUnsavedChanges],
  );

  const handleDescription = useCallback(
    ({ target }) => {
      const description = target.value;
      setIsEdited(false);
      setTagDescription(description);
      setUnsavedChanges(true);
    },
    [setUnsavedChanges],
  );

  const handleEmoji = useCallback(
    (emoji) => {
      setEmoji(emoji);
      setIsEdited(false);
      setIsEmojiSelected(isEmoji(emoji));
      setUnsavedChanges(true);
    },
    [setUnsavedChanges],
  );

  const handleDeleteTag = useCallback(() => {
    setIsEdited(true);
    deleteTag({ id, numberOfClients });
  }, [deleteTag, id, numberOfClients]);

  const handleSaveTag = useCallback(() => {
    setIsEdited(true);
    const savedTag = {
      description: tagDescription,
      emoticon: emoji,
      id,
      name: tagName,
      numberOfClients,
      salonId,
    };
    saveTag({ savedTag });
  }, [saveTag, id, salonId, tagName, emoji, tagDescription, numberOfClients]);

  const handleUndo = useCallback(() => {
    setIsTagSaved(true);
    setDisableSaveBtn(false);
    setEmoji(emoticon);
    setIsEdited(true);
    setIsValidName(true);
    setTagName(name);
    setTagDescription(description);
    setUnsavedChanges(false);
  }, [description, emoticon, name, setIsTagSaved, setUnsavedChanges]);

  const isTrue = isEdited && isTagSaved;
  const isDark = calendarMode === 'dark';

  return (
    <div data-bem="CustomClientTag" className={addTagsLayoutClasses} id={id}>
      <div data-bem="CustomClientTag__content" className={CN.content}>
        <TagName darkTheme={isDark} name={tagName} onChange={handleName} />
        <TagDescription darkTheme={isDark} description={tagDescription} onChange={handleDescription} />
        <EmojiPicker darkTheme={isDark} emoji={emoji} isNew={!emoji} name={name} onChange={handleEmoji} />

        {isTrue ? (
          <DeleteTagButton className={CN.deleteTag} darkTheme={isDark} onClick={handleDeleteTag} tabIndex={0} />
        ) : (
          <>
            <SaveTagButton
              className={CN.saveTag}
              darkTheme={isDark}
              isDisabled={disableSaveBtn || !isValidName || !isEmojiSelected}
              onClick={handleSaveTag}
              tabIndex={0}
            />
            <UndoTagButton className={CN.undoTag} darkTheme={isDark} tabIndex={0} handleUndo={handleUndo} />
          </>
        )}
      </div>
      <div data-bem="CustomClientTag__error" className={CN.error}>
        {!isValidName && <span className={CN.error}>{t('clientTags.nameExists')}</span>}
        {(!isEmojiSelected || disableSaveBtn) && <span className={CN.error}>{t('clientTags.tip')}</span>}
      </div>
    </div>
  );
};
export default CustomClientTag;
